import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Layout } from "./components/layout/Layout"; // Added import
import { ThemeProvider } from "./components/ThemeProvider";
import { AppProvider } from "./components/AppProvider";
import { AuthProvider } from "./hooks/useAuthApi";
import { OrganizationProvider } from "./hooks/useOrganization";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "@/lib/sentry";
import NotFound from "./pages/NotFound";
import Login from "./pages/auth/login";
import SignUp from "./pages/auth/signup";
import ForgotPassword from "./pages/auth/forgot-password";
import ResetPassword from "./pages/auth/reset-password";
import InstallationPage from "./pages/training/InstallationPage";
import AccountSettingsPage from "./pages/platform_views/settings/AccountSettingsPage";
import OrganizationSettingsPage from "./pages/platform_views/settings/OrganizationSettingsPage";
import AcceptInvitationPage from "./pages/auth/AcceptInvitationPage";
import InvitationPage from "./pages/auth/InvitationPage";
import SetPasswordPage from "./pages/auth/SetPasswordPage";
import SessionsPage from "./pages/platform_views/SessionsPage";
import DashboardPage from "./pages/platform_views/DashboardPage";
import AskHobbesPage from "./pages/platform_views/AskHobbesPage";
import { AskHobbesChat } from "./components/dashboard/AskHobbesChat";
import ExtensionAuthSuccess from "./pages/auth/ExtensionAuthSuccess";
import GoogleOAuthCallback from "./pages/auth/GoogleOAuthCallback";
import { SentryErrorFallback } from "./components/SentryErrorFallback";
import GTMTestPage from "./pages/GTMTestPage";
import { SessionManager } from "./components/SessionManager";
import ContentLibraryPage from "./pages/platform_views/ContentLibraryPage";
import PresentationViewerPage from "./pages/platform_views/PresentationViewerPage";
import VideoViewerPage from "./pages/platform_views/VideoViewerPage";
import AgentsPage from "./pages/platform_views/AgentsPage";
import CreateVersionPage from "./pages/platform_views/CreateVersionPage";
import AgentConfigurePage from "./pages/platform_views/AgentConfigurePage";
import TrainingPage from "./pages/platform_views/TrainingPage";
import IntegrationsPage from "./pages/platform_views/IntegrationsPage";
import WebhooksPage from "./pages/platform_views/WebhooksPage";
import CrmSetupPage from "./pages/platform_views/CrmSetupPage";
import AgentPreviewPage from "./pages/AgentPreviewPage";
import InterviewerDetailPage from "./pages/platform_views/InterviewerDetailPage";
import InterviewChatPage from "./pages/InterviewChatPage";
import PeoplePage from "./pages/platform_views/PeoplePage";
import PersonDetailPage from "./pages/platform_views/PersonDetailPage";
import AccountsPage from "./pages/platform_views/AccountsPage";
import AccountDetailPage from "./pages/platform_views/AccountDetailPage";
import PerformancePage from "./pages/platform_views/PerformancePage";
import BackgroundPage from "./pages/platform_views/BackgroundPage";
import DemoStudioPage from "./pages/platform_views/DemoStudioPage";
import DemoStudioSessionPage from "./pages/platform_views/DemoStudioSessionPage";
import SimulationsPage from "./pages/platform_views/SimulationsPage";
import EvaluatorsPage from "./pages/platform_views/EvaluatorsPage";
import ContextPacksPage from "./pages/platform_views/ContextPacksPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 3,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 2,
    },
  },
});

/** Redirect old interviewer detail routes → /agent-knowledge/:id */
const InterviewerDetailRedirect = () => {
  const { interviewerId } = useParams();
  return <Navigate to={`/agent-knowledge/${interviewerId}`} replace />;
};

const queryString = (params: URLSearchParams) => {
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
};

/** Redirect old training routes while preserving evaluation deep links. */
const TrainingRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const rawTab = params.get("tab");
  params.delete("tab");

  if (rawTab === "simulations") {
    return <Navigate to={`/evaluation/simulations${queryString(params)}`} replace />;
  }
  if (rawTab === "evaluators") {
    return <Navigate to={`/evaluation/evaluators${queryString(params)}`} replace />;
  }

  const tabMap: Record<string, string> = {
    overview: "overview",
    knowledge: "knowledge",
    gaps: "gaps",
    sources: "sources",
    interviews: "interviews",
    coaching: "interviews",
    conversations: "interviews",
    sessions: "interviews",
  };
  const nextTab = rawTab ? tabMap[rawTab] : null;
  if (nextTab && nextTab !== "overview") {
    params.set("tab", nextTab);
  }

  return <Navigate to={`/agent-knowledge${queryString(params)}`} replace />;
};

/** Redirect old Knowledge Gaps links while preserving selected gap context. */
const KnowledgeGapsRedirect = () => {
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  const params = new URLSearchParams();
  params.set("tab", "gaps");
  incoming.forEach((value, key) => {
    if (key !== "tab") params.append(key, value);
  });
  return <Navigate to={`/agent-knowledge${queryString(params)}`} replace />;
};

const App = () => {
  return (
    <AppProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="syra-theme">
          <AuthProvider>
            <OrganizationProvider>
              <TooltipProvider delayDuration={300}>
                <Toaster />
                <Sonner />
                <ErrorBoundary fallback={({ error, resetError }) => (
                  <SentryErrorFallback error={error as Error} resetError={resetError} />
                )} showDialog>
                  <BrowserRouter>
                    <SessionManager>
                      <Routes>
                        {/* Auth routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
                        <Route path="/gtm-demo" element={<GTMTestPage />} />
                        {/* Protected routes - all wrapped with Layout */}
                        <Route path="/" element={
                          <ProtectedRoute>
                            <Layout>
                              <DashboardPage />
                            </Layout>
                          </ProtectedRoute>
                        } />
                        <Route
                          path="/installation"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <InstallationPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/account-settings"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AccountSettingsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/organization-settings"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <OrganizationSettingsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/analytics/sessions"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <SessionsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/people"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <PeoplePage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/people/:email"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <PersonDetailPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/accounts"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AccountsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/accounts/:account_domain"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AccountDetailPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/content-library"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <ContentLibraryPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/demo-studio"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <DemoStudioPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/demo-studio/sessions/:sessionId"
                          element={
                            <ProtectedRoute>
                              <DemoStudioSessionPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/content-library/presentation/:presentationId"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <PresentationViewerPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/content-library/video/:videoId"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <VideoViewerPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agents"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AgentsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agents/create-version"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <CreateVersionPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agents/configure"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AgentConfigurePage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agent-knowledge"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <TrainingPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agent-knowledge/:interviewerId"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <InterviewerDetailPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/training" element={<TrainingRedirect />} />
                        <Route path="/training/:interviewerId" element={<InterviewerDetailRedirect />} />
                        <Route
                          path="/performance"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <PerformancePage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/knowledge-gaps"
                          element={<KnowledgeGapsRedirect />}
                        />
                        <Route
                          path="/source-library"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <BackgroundPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/background" element={<Navigate to="/source-library" replace />} />
                        <Route
                          path="/evaluation/simulations"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <SimulationsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/evaluation/evaluators"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <EvaluatorsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/context-packs"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <ContextPacksPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        {/* Redirects from old routes */}
                        <Route path="/sales-agent" element={<Navigate to="/agents" replace />} />
                        <Route path="/interviewer-setup" element={<Navigate to="/agent-knowledge" replace />} />
                        <Route path="/interviewer-setup/:interviewerId" element={<InterviewerDetailRedirect />} />
                        <Route path="/agents/interviewer/:interviewerId" element={<InterviewerDetailRedirect />} />
                        <Route
                          path="/integrations"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <IntegrationsPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/integrations/webhooks"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <WebhooksPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/integrations/:crmId"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <CrmSetupPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/analytics/ask-hobbes"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AskHobbesPage />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ask-hobbes"
                          element={
                            <ProtectedRoute>
                              <Layout>
                                <AskHobbesChat mode="page" />
                              </Layout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/interview/:token"
                          element={<InterviewChatPage />}
                        />
                        <Route
                          path="/preview/:orgId"
                          element={<AgentPreviewPage />}
                        />
                        <Route
                          path="/invite/:token"
                          element={<AcceptInvitationPage />}
                        />
                        <Route
                          path="/invitation"
                          element={<InvitationPage />}
                        />
                        <Route
                          path="/set-password"
                          element={<SetPasswordPage />}
                        />
                        <Route
                          path="/extension/auth-success"
                          element={
                            <ProtectedRoute>
                              <ExtensionAuthSuccess />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </SessionManager>
                  </BrowserRouter>
                </ErrorBoundary>
              </TooltipProvider>
            </OrganizationProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AppProvider>
  );
};

export default App;
